import { useEffect, useMemo, useState } from 'react'
import { Box, Checkbox, Field, Heading, Input, Message } from 'ui'
import { LocationSelector } from 'components/LocationSelector'
import { track } from 'lib/amplitude'
import { useForm, Controller, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { DetailsForm, detailsSchema } from '../../schemas'
import { PositionSelector } from 'components/PositionSelector'
import { DrivingPositionsSelector } from 'components/DrivingPositionsSelector'
import { useCompanyLocation } from 'hooks/useCompanyLocation'
import { Map } from 'components/Map'
import { useShiftEditor } from '../../useShiftEditor'
import { resolveEditorBillRate } from '../../resolveEditorBillRate'
import { Footer } from '../../Footer'
import { AcknowledgeTryoutList } from './AcknowledgeTryoutList'
import { AddLocation } from 'components/AddLocation'
import { AddPosition } from 'components/AddPosition'
import { useLocationCoordinates } from 'hooks/useLocationCoordinates'
import { useIsDrivingPosition } from './useIsDrivingPosition'
import { TryoutLabel } from 'components/tryouts/TryoutLabel'
import { AddPositionTrigger } from 'pages/ShiftEditorPage/pages/DetailsPage/AddPositionTrigger'
import { AddLocationTrigger } from 'pages/ShiftEditorPage/pages/DetailsPage/AddLocationTrigger'
import { ShiftType } from 'pages/ShiftEditorPage/types'
import { useShouldUseBillRate } from 'queries/shift'
import { LegacyProp22MileageBlock } from './LegacyProp22MileageBlock'
import { Prop22MileageBlock } from './Prop22MileageBlock'
import { ConfirmOrderLocations } from './ConfirmOrderLocations'
import { CreateOnDemandDeliveryModal } from './CreateOnDemandDeliveryModal'
import { useGateValue } from '@statsig/react-bindings'
import { FeatureGates } from 'lib/statsig/feature_gates'
import { useCompanyInfo } from 'queries/company'
import { getIsCaliforniaDrivingShift } from 'pages/ShiftEditorPage/useIsCaliforniaDrivingShift'
import { useTrackOnDemandDeliveryPageView } from 'pages/ShiftEditorPage/useTrackOnDemandDeliveryPageView'

export function DetailsPage() {
  useEffect(() => {
    track('impression, ShiftEditor_Details_page')
  }, [])
  useTrackOnDemandDeliveryPageView('Details')

  const {
    shiftData,
    isTryout,
    shiftType,
    setShiftData,
    setShiftType,
    goNext,
    isOnDemandDeliveryBlock,
    isNew,
    editData,
  } = useShiftEditor()
  const originalPositionId = editData?.position?.id
    ? Number(editData.position.id)
    : undefined

  const methods = useForm<DetailsForm>({
    resolver: zodResolver(detailsSchema),
    defaultValues: shiftData?.details,
  })
  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
    setValue,
    register,
    setError,
    getValues,
  } = methods

  const isLocationLess = watch('locationless')
  const selectedLocationId = watch('locationId')
  const selectedLocation = useCompanyLocation(selectedLocationId)
  const coordinates = useLocationCoordinates(selectedLocation)
  const selectedPositionId = watch('positionId')
  const isDrivingPosition = useIsDrivingPosition(selectedPositionId)

  const { data: billRateInfo, isLoading: isLoadingShouldUseBillRate } =
    useShouldUseBillRate(selectedPositionId)
  const shouldUseBillRate = billRateInfo?.isBillPay === true
  const blockShiftCreation = isNew && billRateInfo?.blockShiftCreation === true

  const shouldEnableOnDemandDeliveryShiftCreation = useGateValue(
    FeatureGates.EnableOnDemandDeliveryShiftCreation
  )
  const isW2LeadPositionsEnabled = useGateValue(
    FeatureGates.EnableW2LeadPositions
  )
  const { data: company } = useCompanyInfo()
  const isCompany1099 = !company?.w2
  const canOfferOnDemandDelivery =
    shouldEnableOnDemandDeliveryShiftCreation && isCompany1099

  const [showCreateDeliveryModal, setShowCreateDeliveryModal] = useState(false)

  const isCaliforniaDrivingShift = getIsCaliforniaDrivingShift({
    isDrivingPosition,
    location: selectedLocation,
    isW2: company?.w2,
  })

  // An edit with an unchanged position keeps the shift's own bill-rate mode;
  // creating or switching position follows the position's current setting.
  const getIsBillRate = (currentPositionId?: number) => {
    const { usePositionBillRate } = resolveEditorBillRate({
      isNew,
      originalPositionId,
      currentPositionId,
      shiftBillRate: shiftData.details?.billRate,
      positionBillRate: undefined, // only the flag is needed here
    })
    if (usePositionBillRate) {
      return shouldUseBillRate
    }
    return !!(shiftData.details?.billRateId ?? shiftData.details?.billRate)
  }

  const onSubmit = handleSubmit((data) => {
    if (
      isDrivingPosition &&
      selectedLocation?.address?.state === 'CA' &&
      !company?.w2 &&
      (!data.expectedMileage || data.expectedMileage === 0)
    ) {
      setError('expectedMileage', {
        type: 'custom',
        message: 'Mileage is required for driving positions',
      })
      return
    }

    if (blockShiftCreation) {
      return
    }

    if (
      isCaliforniaDrivingShift &&
      !isOnDemandDeliveryBlock &&
      canOfferOnDemandDelivery
    ) {
      setShowCreateDeliveryModal(true)
      return
    }

    if (
      isOnDemandDeliveryBlock &&
      isCaliforniaDrivingShift &&
      !data.confirmOrderLocations
    ) {
      setError('confirmOrderLocations', {
        type: 'custom',
        message:
          'Please confirm all order locations are within the 50-mile delivery radius',
      })
      return
    }

    setShiftData({
      details: {
        ...data,
        isBillRate: getIsBillRate(data.positionId),
      },
    })
    goNext()
  })

  const handleConvertToDeliveryBlock = () => {
    const data = getValues()
    setShiftData({
      details: {
        ...data,
        isBillRate: getIsBillRate(data.positionId),
      },
    })
    setShiftType(ShiftType.OnDemandDeliveryBlock)
  }

  const title = useMemo(() => {
    if (isTryout) {
      return 'Tryout'
    }

    if (shiftType === ShiftType.LongTermAssignment) {
      return 'Assignment'
    }

    if (shiftType === ShiftType.OnDemandDeliveryBlock) {
      return 'On Demand Delivery Block'
    }

    return 'Shift'
  }, [isTryout, shiftType])

  return (
    <Box>
      <Heading level={[2, 1]} my={4}>
        Tell Us About Your {title}
      </Heading>
      <Box width={[1, 1, '65rem']}>
        <FormProvider {...methods}>
          <form onSubmit={onSubmit}>
            {isTryout ? (
              <Box mb={3}>
                <TryoutLabel />
              </Box>
            ) : null}
            {shiftType === ShiftType.LongTermAssignment ? (
              <Field
                label={
                  <Heading level={3} my={1}>
                    Assignment Name
                  </Heading>
                }
                error={errors.assignmentName?.message?.toString()}
              >
                <Input
                  placeholder="Night Schedule"
                  {...register('assignmentName')}
                />
              </Field>
            ) : null}
            <Field
              label={
                <Heading level={3} my={1}>
                  {isTryout ? '1. Select Tryout Position' : 'Select Position'}
                </Heading>
              }
              cta={
                <AddPosition
                  trigger={<AddPositionTrigger />}
                  onPositionAdded={(positionId) => {
                    setValue('positionId', positionId)
                  }}
                />
              }
              error={errors.positionId?.message?.toString()}
            >
              <Controller
                name={'positionId'}
                control={control}
                render={({ field }) => {
                  const PositionSelectorComponent = isOnDemandDeliveryBlock
                    ? DrivingPositionsSelector
                    : PositionSelector
                  return (
                    <PositionSelectorComponent
                      value={field.value as number}
                      placeholder={
                        isOnDemandDeliveryBlock
                          ? 'Select a driving position'
                          : 'Select a position'
                      }
                      excludeLeadPositions={isW2LeadPositionsEnabled}
                      onChange={(newValue) => {
                        field.onChange(
                          Number((newValue as { value: string }).value)
                        )
                      }}
                    />
                  )
                }}
              />
            </Field>
            {isDrivingPosition &&
            selectedLocation?.address?.state === 'CA' &&
            !company?.w2 ? (
              <Field
                label={'Estimated Driving Distance'}
                error={errors.expectedMileage?.message?.toString()}
              >
                <Input
                  placeholder={'e.g. 20'}
                  {...register('expectedMileage')}
                />

                {isOnDemandDeliveryBlock ? (
                  <Prop22MileageBlock />
                ) : (
                  <LegacyProp22MileageBlock />
                )}
              </Field>
            ) : null}
            {isTryout ? (
              <>
                <Box mt={4}>
                  <Heading level={3} my={1}>
                    2. Acknowledge List
                  </Heading>
                  <AcknowledgeTryoutList />
                </Box>
                {errors.acknowledgeTryoutList?.message ? (
                  <Message variant={'error'} mt={3}>
                    {errors.acknowledgeTryoutList?.message.toString()}
                  </Message>
                ) : null}
              </>
            ) : null}
            <Field
              mt={4}
              label={
                <Heading level={3} my={1}>
                  {isTryout ? '3. Select Tryout Location' : 'Select Location'}
                </Heading>
              }
              cta={
                <AddLocation
                  trigger={<AddLocationTrigger />}
                  onLocationSaved={(locationId) => {
                    setValue('locationId', locationId)
                  }}
                />
              }
              error={errors.locationId?.message?.toString()}
            >
              <Controller
                name={'locationId'}
                control={control}
                render={({ field }) => (
                  <LocationSelector
                    value={field.value as number}
                    placeholder={'Select a location to report to'}
                    onChange={(newValue) => {
                      field.onChange(
                        Number((newValue as { value: string }).value)
                      )
                    }}
                  />
                )}
              />
            </Field>
            <Controller
              name={'locationless'}
              control={control}
              render={({ field }) => (
                <Checkbox {...field}>
                  Worker does not need to report to this location
                </Checkbox>
              )}
            />
            {isLocationLess ? (
              <Message variant={'info'} mt={3}>
                You must enter a location even if your workers do not need to
                report to a location. We use the location you enter to determine
                the timezone of your shift.
              </Message>
            ) : null}
            {isOnDemandDeliveryBlock &&
            coordinates &&
            isCaliforniaDrivingShift ? (
              <ConfirmOrderLocations
                coordinates={coordinates}
                locationName={selectedLocation?.name}
              />
            ) : coordinates ? (
              <Box mt={3}>
                <Map
                  center={coordinates}
                  zoom={11}
                  markers={[
                    {
                      position: coordinates,
                      title: selectedLocation?.name ?? 'Selected Location',
                    },
                  ]}
                />
              </Box>
            ) : null}
            {blockShiftCreation ? (
              <Message variant="error" mt={3}>
                This position requires a bill rate before shifts can be created.
                Please contact WorkWhile Support or your Account Manager.
              </Message>
            ) : null}
            <Footer
              isLoading={isLoadingShouldUseBillRate}
              disabled={blockShiftCreation}
            />
          </form>
        </FormProvider>
      </Box>
      <CreateOnDemandDeliveryModal
        open={showCreateDeliveryModal}
        onCreate={handleConvertToDeliveryBlock}
        onClose={() => setShowCreateDeliveryModal(false)}
      />
    </Box>
  )
}
